/**
 * Централизованный HTTP-клиент EyeHub.
 *
 * Axios instance с interceptors:
 *   - локализация запросов (?language=);
 *   - единая обработка 401 (истечение сессии) с объяснением и запоминанием
 *     адреса возврата;
 *   - единая обработка 403 (нет прав) с сообщением бэкенда, а не статичным
 *     текстом;
 *   - проброс критических ошибок (5xx/сеть) в UI через `window.onCriticalError`.
 *
 * Здесь же — единственное место, где пишется и читается `returnUrl`: раньше он
 * записывался в двух местах (client.js и Authmiddleware) и не читался нигде.
 *
 * @module api/client
 */

import axios from "axios";
import { toast } from "react-toastify";
import { parseHttpError, isCriticalError } from "../utils/errorHandler";
import { extractApiError } from "../utils/toastApiError";
import i18n, { getCurrentApiLocale } from "../i18n";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1/";

/** Ключ sessionStorage с адресом, куда вернуть пользователя после входа. */
export const RETURN_URL_KEY = "returnUrl";
/** Ключ sessionStorage-флага «сессия истекла» (для сообщения на /login). */
export const SESSION_EXPIRED_KEY = "sessionExpired";

/**
 * Отбрасывает небезопасные и бессмысленные адреса возврата.
 * Допускается только относительный путь: `/что-то` без `//` (иначе это
 * protocol-relative URL на чужой хост) и не страница входа/выхода.
 *
 * @param {*} value
 * @returns {string|null}
 */
export const sanitizeReturnUrl = (value) => {
  if (typeof value !== "string" || !value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  if (/^\/(login|logout|auth\/)/.test(value)) return null;
  return value;
};

/** Запоминает адрес возврата (единственная точка записи). */
export const rememberReturnUrl = (path) => {
  const safe = sanitizeReturnUrl(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(RETURN_URL_KEY, safe);
  } catch (_) {
    /* Safari private mode */
  }
};

/** Читает и удаляет адрес возврата (единственная точка чтения). */
export const consumeReturnUrl = () => {
  try {
    const value = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    return sanitizeReturnUrl(value);
  } catch (_) {
    return null;
  }
};

/** Ставит флаг «сессия истекла». */
export const markSessionExpired = () => {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch (_) {
    /* Safari private mode */
  }
};

/** Читает и снимает флаг «сессия истекла». */
export const consumeSessionExpired = () => {
  try {
    const flag = sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return flag;
  } catch (_) {
    return false;
  }
};

/**
 * Основной axios instance. Все API-запросы идут через него.
 * withCredentials — cookie-сессия BFF (eyehub_session).
 */
export const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 30000, // 30 сек — предотвращает зависание при медленном соединении
});

/**
 * Перехватчик запросов: добавляет ?language= для билингвального API.
 */
apiClient.interceptors.request.use(
  (config) => {
    const locale = getCurrentApiLocale();
    config.params = { ...config.params, language: locale };
    return config;
  },
  (error) => Promise.reject(error)
);

// Одноразовые guard'ы: несколько параллельных 401/403 не должны давать
// несколько редиректов и стопку одинаковых toast'ов.
let sessionExpiryHandled = false;

/** Сбрасывает guard истечения сессии (используется в тестах). */
export const __resetSessionExpiryGuard = () => {
  sessionExpiryHandled = false;
};

/** Код ошибки из unified-envelope бэкенда (`{error: {code}}`). */
const extractApiCode = (body) => body?.error?.code ?? body?.code ?? null;

/**
 * Перехватчик ответов: 401 (сессия), 403 (права), критические ошибки.
 *
 * Сообщение бэкенда больше не выбрасывается: `error.message` собирается тем же
 * `extractApiError`, что и `toastApiError`, поэтому catch-блоки страниц и
 * глобальные toast'ы показывают один и тот же текст сервера.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const body = error.response?.data;

    // --- 401: сессия истекла -------------------------------------------------
    if (status === 401) {
      const currentPath = window.location.pathname + window.location.search;
      const isAuthPage =
        currentPath.startsWith("/login") || currentPath.startsWith("/auth/callback");

      if (!isAuthPage && !sessionExpiryHandled) {
        sessionExpiryHandled = true;
        rememberReturnUrl(currentPath);
        markSessionExpired();

        // Шелл (Authmiddleware) показывает модалку с объяснением; если
        // обработчик не смонтирован — деградируем в прежний жёсткий редирект.
        if (typeof window.onSessionExpired === "function") {
          window.onSessionExpired({ returnUrl: sanitizeReturnUrl(currentPath) || "" });
        } else {
          window.location.replace("/login");
        }
      }
      // На auth-страницах ошибка пропускается дальше (форма покажет её сама).
    }

    // --- 403: недостаточно прав ----------------------------------------------
    if (status === 403) {
      const denied = i18n.t("permission.denied", {
        ns: "errors",
        defaultValue: "Недостаточно прав для этого действия",
      });
      const code = extractApiCode(body);
      const message = extractApiError({ status, data: body }, denied);
      toast.error(code ? `${message} (${code})` : message, { toastId: "eyehub-forbidden" });
    }

    // Сообщение сервера вместо статичного текста.
    const message = extractApiError({ status, data: body }, parseHttpError(error));
    const parsedError = new Error(message);
    parsedError.originalError = error;
    parsedError.response = error.response;
    parsedError.status = status;
    parsedError.data = body;
    parsedError.code = extractApiCode(body);
    parsedError.isCritical = isCriticalError(error);

    if (parsedError.isCritical) {
      logger.error("Critical API error:", {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status,
        message: parsedError.message,
      });
      if (typeof window.onCriticalError === "function") {
        window.onCriticalError(parsedError);
      }
    }

    return Promise.reject(parsedError);
  }
);

/** GET-хелпер. Возвращает response.data. */
export const get = (url, config = {}) =>
  apiClient.get(url, config).then((response) => response.data);

/** POST-хелпер. Возвращает response.data. */
export const post = (url, data, config = {}) =>
  apiClient.post(url, data, config).then((response) => response.data);

/** PUT-хелпер. Возвращает response.data. */
export const put = (url, data, config = {}) =>
  apiClient.put(url, data, config).then((response) => response.data);

/** PATCH-хелпер. Возвращает response.data. */
export const patch = (url, data, config = {}) =>
  apiClient.patch(url, data, config).then((response) => response.data);

/** DELETE-хелпер. Возвращает response.data. */
export const del = (url, config = {}) =>
  apiClient.delete(url, config).then((response) => response.data);

export default apiClient;
